import axiosInstanceAI from "@/api/axiosInstanceAI";
import axiosInstanceNode from "@/api/axiosInstanceNode";

/** AI people search runs several web searches; the AI backend gives up at 270s. */
const AI_PEOPLE_SEARCH_TIMEOUT_MS = 300_000;

export const fetchContactSearchParams = async ({
  user_id,
  source,
  campaign_id,
  query,
}) => {
  const payload = {
    user_id,
    source,
  };
  if (campaign_id) payload.campaign_id = campaign_id;
  if (query) payload.query = query;
  const response = await axiosInstanceAI.post("/contact-search/search-params", payload);
  return response.data;
};

export const draftProspectSearchQueryFromCampaign = async ({
  user_id,
  campaign_id,
}) => {
  const response = await axiosInstanceAI.post(
    "/prospect-search/campaign-query-draft",
    {
      user_id,
      campaign_id,
    },
  );
  return response.data;
};

/** An error shaped like an axios one, so the existing error toasts read it unchanged. */
const searchError = (status, detail, code) =>
  Object.assign(new Error(detail || `HTTP ${status}`), { code, response: { status, data: { detail } } });

/**
 * POST /contact-search/ai-search/stream: the same search, but people arrive as soon as each is
 * verified. `onPeople(batch)` gets every new batch; resolves with the final result (the
 * /ai-search response shape). Aborting `signal` (or the 300s timeout) stops the search.
 */
export const aiPeopleSearchStream = async ({ onPeople, signal, ...search }) => {
  const { user_id, source, query, campaign_id, filters, exclude, page } = search;
  const payload = { user_id, source, filters: filters || {}, exclude: exclude || [] };
  if (page > 1) payload.page = page;
  if (campaign_id) payload.campaign_id = campaign_id;
  if (query) payload.query = query;
  const baseURL = axiosInstanceAI.defaults.baseURL || process.env.REACT_APP_AI_BASE_URL;
  const token = localStorage.getItem("token");
  const timeout = new AbortController();
  const timer = setTimeout(() => timeout.abort(), AI_PEOPLE_SEARCH_TIMEOUT_MS);
  signal?.addEventListener("abort", () => timeout.abort());
  try {
    const response = await fetch(`${baseURL}/contact-search/ai-search/stream`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(token && { Authorization: `Bearer ${token}` }) },
      body: JSON.stringify(payload),
      signal: timeout.signal,
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw searchError(response.status, typeof body.detail === "string" ? body.detail : undefined);
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let done = null;
    const handle = async (line) => {
      if (!line.trim()) return;
      const event = JSON.parse(line);
      if (event.type === "people") await onPeople?.(event.people || []);
      else if (event.type === "error") throw searchError(event.status, event.detail);
      else if (event.type === "done") {
        const { type: _type, ...result } = event;
        done = result;
      }
    };
    while (true) {
      const { done: finished, value } = await reader.read();
      if (finished) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";
      for (const line of lines) await handle(line);
    }
    await handle(buffer);
    if (!done) throw searchError(502, "The search stopped before it finished. Please try again.");
    return done;
  } catch (error) {
    if (error.name === "AbortError" && !signal?.aborted) {
      throw searchError(504, undefined, "ECONNABORTED");
    }
    throw error;
  } finally {
    clearTimeout(timer);
  }
};

/** POST /contact-search/availability: which LinkedIn profiles have a work email / phone (free check). */
export const fetchContactAvailability = async ({ linkedinUrls, checkPhone = false }) => {
  const response = await axiosInstanceNode.post("/contact-search/availability", {
    linkedin_urls: linkedinUrls,
    check_phone: checkPhone,
  });
  return response.data?.results || {};
};

/** POST /contact-search/suggestions: example searches written from the user's company profile. */
export const fetchSearchSuggestions = async ({ user_id, kind }) => {
  const response = await axiosInstanceAI.post("/contact-search/suggestions", { user_id, kind });
  return response.data;
};

/** Saved contact searches (the Find page's "Your Saved Searches"). */
export const fetchSavedProspectSearches = async () => {
  const response = await axiosInstanceNode.get("/contact-search/saved");
  return response.data?.data ?? [];
};

export const saveProspectSearchApi = async (search) => {
  const response = await axiosInstanceNode.post("/contact-search/saved", search);
  return response.data?.data;
};

export const deleteSavedProspectSearchApi = async (id) => {
  await axiosInstanceNode.delete(`/contact-search/saved/${id}`);
};

export const fetchProspectsByQuery = async (filters) => {
  const response = await axiosInstanceNode.post("/contact-search/search", filters);
  return response.data;
};

export const importFromContactOut = async ({ prospects, campaign_ids, include_phone = false }) => {
  const payload = { prospects, include_phone };
  if (Array.isArray(campaign_ids) && campaign_ids.length > 0) {
    payload.campaign_ids = campaign_ids;
  }
  const response = await axiosInstanceNode.post("/contact/import-from-contactout", payload);
  return response.data;
};

// Temporary compatibility exports for existing module consumers during the UI rollout.
export const fetchApolloSearchParams = fetchContactSearchParams;
export const importFromApollo = importFromContactOut;

/** POST /prospect-search/query — NL → Explorium people search (AI backend). */
export const searchProspectsByNaturalQuery = async ({
  query,
  user_id,
  size = 10,
}) => {
  const response = await axiosInstanceAI.post("/prospect-search/query", {
    type_is: "query",
    query,
    user_id: user_id || undefined,
    size,
  });
  return response.data;
};

/** POST /prospect-search/query — campaign ICP → Explorium people search. */
export const searchProspectsByCampaignIcp = async ({
  campaign_id,
  user_id,
  query,
  size = 10,
}) => {
  const payload = {
    type_is: "campaign_icp",
    campaign_id,
    user_id: user_id || undefined,
    size,
  };
  if (query) payload.query = query;
  const response = await axiosInstanceAI.post("/prospect-search/query", payload);
  return response.data;
};

/** POST /prospect-search/query/load-more — paginate without re-running intent LLM. */
export const loadMoreProspects = async ({
  query_id,
  next_cursor,
  user_id,
  size = 10,
}) => {
  const response = await axiosInstanceAI.post("/prospect-search/query/load-more", {
    query_id,
    next_cursor,
    user_id: user_id || undefined,
    size,
  });
  return response.data;
};

/** POST /prospect-search/prospects/enrich — reveal email/phone + import rows. */
export const enrichProspects = async ({ prospects, fields_requested, user_id }) => {
  const response = await axiosInstanceAI.post("/prospect-search/prospects/enrich", {
    prospects,
    fields_requested,
    user_id,
  });
  return response.data;
};

/** POST /contact/import-from-prospect-search-enriched — persist enrich rows into core contacts (+ optional campaigns). */
export const importProspectSearchEnrichedContacts = async ({
  imported,
  campaign_ids,
}) => {
  const payload = { imported };
  if (Array.isArray(campaign_ids) && campaign_ids.length > 0) {
    payload.campaign_ids = campaign_ids;
  }
  const response = await axiosInstanceNode.post(
    "/contact/import-from-prospect-search-enriched",
    payload,
  );
  return response.data;
};

export const fetchProspectSearchThreads = async ({ user_id }) => {
  const response = await axiosInstanceAI.get("/prospect-search/chat/threads", {
    params: { user_id },
  });
  return response.data;
};

export const createProspectSearchThread = async ({ user_id, title }) => {
  const response = await axiosInstanceAI.post("/prospect-search/chat/threads", {
    user_id,
    title,
  });
  return response.data;
};

export const deleteProspectSearchThread = async ({ user_id, thread_id }) => {
  await axiosInstanceAI.delete(`/prospect-search/chat/threads/${thread_id}`, {
    params: { user_id },
  });
};

export const fetchProspectSearchThreadMessages = async ({ user_id, thread_id }) => {
  const response = await axiosInstanceAI.get(
    `/prospect-search/chat/threads/${thread_id}/messages`,
    { params: { user_id } },
  );
  return response.data;
};

export const sendProspectSearchChatMessage = async ({
  user_id,
  thread_id,
  prompt,
  display_prompt,
  selected_prospect_ids = [],
  selected_prospects = [],
  campaign_id,
  campaign_name,
}) => {
  const baseURL =
    axiosInstanceAI.defaults.baseURL || process.env.REACT_APP_AI_BASE_URL;
  const token = localStorage.getItem("token");
  const response = await fetch(
    `${baseURL}/prospect-search/chat/threads/${thread_id}/messages`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify({
        user_id,
        prompt,
        ...(display_prompt && { display_prompt }),
        selected_prospect_ids,
        selected_prospects,
        ...(campaign_id && { campaign_id }),
        ...(campaign_name && { campaign_name }),
      }),
    },
  );
  if (!response.ok) {
    throw new Error((await response.text()) || `HTTP ${response.status}`);
  }
  return response.body;
};
